/**
 * 结算页面（三步流程）- 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 *
 * 步骤 1：收货地址（选择已保存地址 或 填写新地址）
 * 步骤 2：订单确认 + 优惠码
 * 步骤 3：支付方式选择 + 提交订单
 *
 * 未登录用户将被跳转到 /login（App.js 通过 RequireAuth 已处理，此处作双重防护）
 * 数据来源：useCart().cart、customerApi.getAddresses()、orderApi.checkout()、orderApi.pay()
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Steps,
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Radio,
  Space,
  Spin,
  Tag,
  message,
  Alert,
  Modal,
  Checkbox,
} from 'antd';
import {
  CheckCircleOutlined,
  PlusOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { customerApi, orderApi, discountApi } from '../../api/index';

const { Title, Text } = Typography;
const { Step } = Steps;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { cart, refreshCart } = useCart();

  // 当前步骤（0=地址, 1=订单确认, 2=支付）
  const [currentStep, setCurrentStep] = useState(0);

  // ===== 步骤 1：地址 =====
  const [addresses, setAddresses] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  // 是否显示新地址表单
  const [showNewAddr, setShowNewAddr] = useState(false);
  const [addrForm] = Form.useForm();

  // ===== 步骤 2：优惠码 =====
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null); // { amount, type, ... }
  const [discountLoading, setDiscountLoading] = useState(false);

  // ===== 步骤 3：支付 =====
  const [payVendor, setPayVendor] = useState('unionpay'); // 默认银联支付
  const [submitting, setSubmitting] = useState(false);

  // 最终结算地址对象（从已保存地址或新地址表单获取）
  const [shippingAddress, setShippingAddress] = useState(null);

  // 是否保存新地址到地址簿
  const [saveToAddressBook, setSaveToAddressBook] = useState(false);

  // 游客邮箱（未登录时必填，作为订单确认与结果查询的联系方式）
  const [guestEmail, setGuestEmail] = useState('');

  // 页面加载时：刷新购物车、获取地址、自动应用折扣
  useEffect(() => {
    refreshCart();
    if (isLoggedIn) {
      // 已登录：拉取地址簿
      fetchAddresses();
    } else {
      // 游客：无地址簿，直接进入新地址表单
      setShowNewAddr(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 购物车加载后，自动应用折扣（传入商品 ID 列表和数量）
  useEffect(() => {
    if (cart?.subtotal && !discountInfo && !discountCode) {
      // 自动应用折扣（传入完整的 items 数组，包含数量信息）
      discountApi.applyAuto(cart.subtotal, cart?.items || []).then((discount) => {
        if (discount && discount.id) {
          // 计算折扣金额（后端已改为元为单位）
          // 商品限定折扣仅作用于适用商品的小计
          const items = cart?.items || [];
          let base = cart.subtotal;
          if (discount.applyToProducts) {
            const ids = String(discount.applyToProducts)
              .split(',')
              .map((s) => parseInt(s.trim(), 10))
              .filter((n) => !Number.isNaN(n));
            base = items
              .filter((it) => ids.includes(it.productId))
              .reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 0), 0);
          }
          let amount = 0;
          if (discount.type === 'percentage') {
            amount = base * discount.value / 100;
          } else if (discount.type === 'fixed_amount') {
            amount = Math.min(discount.value, base);
          } else if (discount.type === 'free_shipping') {
            // 免运费折扣
          }
          if (amount > 0 || discount.type === 'free_shipping') {
            setDiscountInfo({ amount, type: discount.type, discount });
            // 自动折扣不显示优惠码
            setDiscountCode('');
          }
        }
      }).catch(() => {
        // 无自动折扣，忽略
      });
    }
  }, [cart?.subtotal, cart?.items, discountInfo, discountCode]);

  /** 获取用户已保存地址列表 */
  const fetchAddresses = async () => {
    setAddrLoading(true);
    try {
      const data = await customerApi.getAddresses();
      setAddresses(data || []);
      // 若有地址，默认选中第一条（默认地址优先）
      if (data && data.length > 0) {
        const defaultAddr = data.find(a => a.isDefault);
        setSelectedAddressId(defaultAddr ? defaultAddr.id : data[0].id);
      } else {
        // 无保存地址直接显示新地址表单
        setShowNewAddr(true);
      }
    } catch {
      setShowNewAddr(true);
    } finally {
      setAddrLoading(false);
    }
  };

  // 格式化价格：直接显示美元（后端已改为元为单位）
  const formatPrice = (amount) => `$${(amount || 0).toFixed(2)}`;

  // ===== 价格计算 =====
  const subtotal = cart?.subtotal || 0;
  const shipping = 0; // 暂定免运费
  const discountAmount = discountInfo?.amount || 0;
  const total = Math.max(0, subtotal + shipping - discountAmount);

  // ===== 步骤 1：确认地址 =====
  const handleConfirmAddress = async () => {
    // 游客必须填写有效邮箱
    if (!isLoggedIn) {
      const email = guestEmail.trim();
      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!emailValid) {
        message.warning('请填写有效的邮箱地址');
        return;
      }
    }
    if (showNewAddr) {
      // 验证新地址表单
      try {
        const values = await addrForm.validateFields();
        // 如果勾选保存地址，立即保存到地址簿（不管下单是否成功）
        if (saveToAddressBook && isLoggedIn) {
          try {
            const savedAddr = await customerApi.addAddress({
              firstName: values.firstName,
              lastName: values.lastName,
              addressLine1: values.address1,
              city: values.city,
              province: values.province,
              phone: values.phone,
              isDefault: addresses.length === 0, // 如果是第一个地址，设为默认
            });
            // 使用已保存的地址（含 ID）
            setShippingAddress(savedAddr);
            message.success('地址已保存到地址簿');
          } catch (err) {
            // 保存失败不影响下单，继续使用新地址
            setShippingAddress(values);
            message.warning('地址保存失败，但可以继续下单');
          }
        } else {
          setShippingAddress(values);
        }
      } catch {
        return;
      }
    } else {
      // 使用已选中的保存地址
      const found = addresses.find((a) => a.id === selectedAddressId);
      if (!found) {
        message.warning('请选择收货地址');
        return;
      }
      setShippingAddress(found);
    }
    setCurrentStep(1);
  };

  // ===== 步骤 2：应用优惠码 =====
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      message.warning('请输入优惠码');
      return;
    }
    setDiscountLoading(true);
    try {
      const items = cart?.items || [];
      const discount = await discountApi.validate(discountCode.trim(), subtotal, items);
      // /validate 仅返回 Discount 实体（type/value/...），不含计算后的 amount，
      // 此处按类型计算实际优惠金额，与自动折扣分支保持一致。
      // 商品限定折扣仅作用于适用商品的小计，避免非适用商品也被打折。
      let base = subtotal;
      if (discount?.applyToProducts) {
        const ids = String(discount.applyToProducts)
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !Number.isNaN(n));
        base = items
          .filter((it) => ids.includes(it.productId))
          .reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 0), 0);
      }
      let amount = 0;
      if (discount?.type === 'percentage') {
        amount = base * (discount.value || 0) / 100;
      } else if (discount?.type === 'fixed_amount') {
        amount = Math.min(discount.value || 0, base);
      }
      // free_shipping 类型不在小计中体现优惠金额，amount 保持 0
      setDiscountInfo({ amount, type: discount?.type, discount });
      if (discount?.type === 'free_shipping') {
        message.success('优惠码已应用，免运费');
      } else {
        message.success(`优惠码已应用，优惠 ${formatPrice(amount)}`);
      }
    } catch (err) {
      message.error(err?.response?.data?.message || '优惠码无效或已过期');
      setDiscountInfo(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  // ===== 步骤 3：提交订单并支付 =====
  const handlePlaceOrder = async () => {
    setSubmitting(true);
    try {
      // 调用结算接口创建弃单（返回 abandonedOrderId）
      // 已保存地址用 shippingAddressId，新地址把字段平铺
      const addrPayload = shippingAddress?.id
        ? { shippingAddressId: shippingAddress.id }
        : { ...shippingAddress };
      const orderResult = await orderApi.checkout({
        ...addrPayload,
        // 游客下单需带上邮箱（登录用户由后端取账号邮箱）
        email: !isLoggedIn ? guestEmail.trim() : undefined,
        discountCode: discountInfo ? discountCode : undefined,
        vendor: payVendor,
      });

      // 新流程返回 abandonedOrderId 和 abandonedOrderNo
      const abandonedOrderId = orderResult?.abandonedOrderId;
      const abandonedOrderNo = orderResult?.abandonedOrderNo;
      if (!abandonedOrderId) {
        message.error('订单创建失败');
        return;
      }

      // 调用支付接口获取支付 URL（使用 abandonedOrderId）
      const payResult = await orderApi.pay(abandonedOrderId, payVendor);

      if (payResult?.payUrl) {
        if (payResult.formFields && Object.keys(payResult.formFields).length > 0) {
          // POST 表单类（银联等）：按 NihaoPay form 对象构造表单并提交
          const form = document.createElement('form');
          form.method = payResult.formMethod || 'POST';
          form.action = payResult.payUrl;
          form.target = payResult.formTarget || '_self';
          Object.entries(payResult.formFields).forEach(([k, v]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = k;
            input.value = v;
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
        } else {
          // GET 跳转类（支付宝、微信等）
          window.location.href = payResult.payUrl;
        }
      } else {
        // 无 payUrl 时跳转结果页
        navigate(`/checkout/result?reference=${payResult?.reference || abandonedOrderNo}&abandonedOrderNo=${abandonedOrderNo}`);
      }
    } catch (err) {
      message.error(err?.response?.data?.message || '下单失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 购物车商品列表
  const cartItems = cart?.items || [];

  // ===== 渲染各步骤内容 =====

  /** 步骤 1 内容：收货地址 */
  const renderStep1 = () => (
    <div>
      {addrLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin tip="加载地址..." />
        </div>
      ) : (
        <>
          {/* 游客邮箱（未登录时必填，用于接收订单确认与查询订单） */}
          {!isLoggedIn && (
            <div style={{ marginBottom: 24 }}>
              <Title level={5} style={{ marginBottom: 8, color: COLORS.ink }}>联系邮箱</Title>
              <Input
                type="email"
                placeholder="用于接收订单确认，例如 name@example.com"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                size="large"
                style={{ borderRadius: 8 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                下单后可用此邮箱查询订单，或
                <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => navigate('/login')}>
                  登录
                </Button>
                以使用地址簿。
              </Text>
            </div>
          )}

          {/* 已保存地址列表 */}
          {addresses.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <Title level={5} style={{ marginBottom: 16, color: COLORS.ink }}>选择收货地址</Title>
              <Radio.Group
                value={selectedAddressId}
                onChange={(e) => {
                  setSelectedAddressId(e.target.value);
                  setShowNewAddr(false);
                }}
                style={{ width: '100%' }}
              >
                <Row gutter={[16, 16]}>
                  {addresses.map((addr) => (
                    <Col key={addr.id} xs={24} sm={12}>
                      <Card
                        size="small"
                        style={{
                          cursor: 'pointer',
                          border: selectedAddressId === addr.id && !showNewAddr
                            ? `2px solid ${COLORS.teaBrown}`
                            : `1px solid ${COLORS.lightBorder}`,
                          borderRadius: 10,
                        }}
                        onClick={() => {
                          setSelectedAddressId(addr.id);
                          setShowNewAddr(false);
                        }}
                      >
                        <Radio value={addr.id} style={{ width: '100%' }}>
                          <div>
                            <Text strong style={{ color: COLORS.ink }}>
                              {addr.lastName} {addr.firstName}
                            </Text>
                            {addr.isDefault && (
                              <Tag
                                color={COLORS.bambooGreen}
                                style={{
                                  marginLeft: 8,
                                  background: `${COLORS.bambooGreen}15`,
                                  borderColor: COLORS.bambooGreen,
                                  color: COLORS.bambooGreen,
                                }}
                              >
                                默认
                              </Tag>
                            )}
                            <div style={{ marginTop: 4, color: COLORS.lightInk, fontSize: 13 }}>
                              {addr.phone}
                            </div>
                            <div style={{ color: COLORS.lightInk, fontSize: 13 }}>
                              {addr.address1}
                              {addr.address2 && `, ${addr.address2}`}
                            </div>
                            <div style={{ color: COLORS.lightInk, fontSize: 13 }}>
                              {[addr.province, addr.city]
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                          </div>
                        </Radio>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Radio.Group>

              {/* 使用新地址切换按钮 */}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                style={{
                  marginTop: 12,
                  borderRadius: 8,
                  borderColor: COLORS.teaBrown,
                  color: COLORS.teaBrown,
                }}
                onClick={() => {
                  setShowNewAddr(true);
                  setSelectedAddressId(null);
                }}
              >
                使用新地址
              </Button>
            </div>
          )}

          {/* 新地址表单 */}
          {showNewAddr && (
            <div>
              <Title level={5} style={{ marginBottom: 16 }}>
                {addresses.length > 0 ? '填写新收货地址' : '填写收货地址'}
              </Title>
              <Form form={addrForm} layout="vertical">
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="lastName"
                      label="姓氏"
                      rules={[{ required: true, message: '请输入姓氏' }]}
                    >
                      <Input placeholder="姓氏" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="firstName"
                      label="名字"
                      rules={[{ required: true, message: '请输入名字' }]}
                    >
                      <Input placeholder="名字" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="phone"
                  label="电话"
                  rules={[{ required: true, message: '请输入联系电话' }]}
                >
                  <Input placeholder="联系电话" />
                </Form.Item>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="province"
                      label="省份"
                    >
                      <Input placeholder="省份" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="city"
                      label="城市"
                      rules={[{ required: true, message: '请输入城市' }]}
                    >
                      <Input placeholder="城市" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="address1"
                  label="街道地址"
                  rules={[{ required: true, message: '请输入街道地址' }]}
                >
                  <Input placeholder="街道、门牌号" />
                </Form.Item>
                <Form.Item name="saveAddress" valuePropName="checked">
                  <Checkbox checked={saveToAddressBook} onChange={(e) => setSaveToAddressBook(e.target.checked)}>
                    保存到地址簿，下次下单可直接选择
                  </Checkbox>
                </Form.Item>
              </Form>
            </div>
          )}
        </>
      )}

      <Button
        type="primary"
        size="large"
        onClick={handleConfirmAddress}
        style={{
          marginTop: 16,
          borderRadius: 8,
          background: COLORS.teaBrown,
          borderColor: COLORS.teaBrown,
          height: 48,
        }}
      >
        下一步：确认订单
      </Button>
    </div>
  );

  /** 步骤 2 内容：订单确认 + 优惠码 */
  const renderStep2 = () => (
    <div>
      {/* 收货地址摘要 */}
      {shippingAddress && (
        <Card
          size="small"
          style={{
            marginBottom: 24,
            borderRadius: 10,
            borderColor: COLORS.lightBorder,
          }}
        >
          <Text strong style={{ color: COLORS.ink }}>收货地址：</Text>
          <Text style={{ marginLeft: 8, color: COLORS.lightInk }}>
            {shippingAddress.lastName} {shippingAddress.firstName}，
            {shippingAddress.phone}，
            {shippingAddress.address1}，
            {[shippingAddress.province, shippingAddress.city]
              .filter(Boolean)
              .join('，')}
          </Text>
          <Button
            type="link"
            size="small"
            onClick={() => setCurrentStep(0)}
            style={{ color: COLORS.teaBrown }}
          >
            修改
          </Button>
        </Card>
      )}

      {/* 订单商品列表 */}
      <Title level={5} style={{ marginBottom: 16, color: COLORS.ink }}>订单商品</Title>
      {cartItems.map((item) => (
        <Row key={item.id} justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col flex={1}>
            <Text>{item.productTitle}</Text>
            {(item.variantTitle || item.option1) && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                {item.variantTitle || [item.option1, item.option2].filter(Boolean).join('/')}
              </Text>
            )}
            <Text type="secondary" style={{ marginLeft: 8 }}>
              ×{item.quantity}
            </Text>
          </Col>
          <Col>
            <Text strong>{formatPrice((item.price || 0) * (item.quantity || 1))}</Text>
          </Col>
        </Row>
      ))}

      <Divider />

      {/* 优惠码输入区 */}
      <Title level={5} style={{ marginBottom: 12, color: COLORS.ink }}>
        优惠码 {discountInfo && !discountCode && <Tag color={COLORS.bambooGreen} style={{ background: `${COLORS.bambooGreen}15`, borderColor: COLORS.bambooGreen, color: COLORS.bambooGreen }}>已有优惠</Tag>}
      </Title>

      {/* 已应用折扣提示 */}
      {discountInfo && (
        <Alert
          type="success"
          showIcon
          message={
            <div>
              <strong>{discountInfo.discount && !discountCode ? '自动优惠：' + (discountInfo.discount?.title || '') : (discountInfo?.discount?.title || discountCode)}</strong>
              <span style={{ marginLeft: 8 }}>已生效，节省 {formatPrice(discountInfo.amount)}</span>
            </div>
          }
          description={
            discountInfo.discount?.type === 'percentage' ? (
              <Text style={{ fontSize: 12, color: COLORS.lightInk }}>
                {discountInfo.discount.value}% 折扣
              </Text>
            ) : discountInfo.discount?.type === 'fixed_amount' ? (
              <Text style={{ fontSize: 12, color: COLORS.lightInk }}>
                立减 {formatPrice(discountInfo.discount.value)}
              </Text>
            ) : discountInfo.discount?.type === 'free_shipping' ? (
              <Text style={{ fontSize: 12, color: COLORS.lightInk }}>
                免运费优惠
              </Text>
            ) : null
          }
          closable
          onClose={() => {
            setDiscountInfo(null);
            setDiscountCode('');
          }}
          style={{
            marginBottom: 16,
            borderRadius: 8,
            borderColor: COLORS.bambooGreen,
          }}
        />
      )}

      {!discountInfo && (
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Input
            placeholder="请输入优惠码"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
            onPressEnter={handleApplyDiscount}
            style={{ borderRadius: 8 }}
          />
          <Button
            type="primary"
            onClick={handleApplyDiscount}
            loading={discountLoading}
            style={{
              borderRadius: 8,
              background: COLORS.teaBrown,
              borderColor: COLORS.teaBrown,
            }}
          >
            应用
          </Button>
        </Space.Compact>
      )}

      {/* 手动输入优惠码按钮（已有自动优惠时） */}
      {discountInfo && !discountCode && (
        <Button
          type="dashed"
          onClick={() => {
            setDiscountInfo(null);
            setDiscountCode('');
          }}
          style={{
            marginBottom: 16,
            borderRadius: 8,
            borderColor: COLORS.teaBrown,
            color: COLORS.teaBrown,
          }}
        >
          使用其他优惠码
        </Button>
      )}

      {/* 价格汇总 */}
      <Card
        size="small"
        style={{
          marginBottom: 24,
          borderRadius: 10,
          borderColor: COLORS.lightBorder,
        }}
      >
        <Row justify="space-between" style={{ marginBottom: 8 }}>
          <Col><Text style={{ color: COLORS.lightInk }}>商品小计</Text></Col>
          <Col><Text strong style={{ color: COLORS.ink }}>{formatPrice(subtotal)}</Text></Col>
        </Row>
        <Row justify="space-between" style={{ marginBottom: 8 }}>
          <Col><Text style={{ color: COLORS.lightInk }}>运费</Text></Col>
          <Col><Text style={{ color: COLORS.bambooGreen }}>到付</Text></Col>
        </Row>
        {discountAmount > 0 && (
          <Row justify="space-between" style={{ marginBottom: 8 }}>
            <Col><Text style={{ color: COLORS.lightInk }}>优惠折扣</Text></Col>
            <Col><Text type="success" style={{ color: COLORS.cinnabar }}>-{formatPrice(discountAmount)}</Text></Col>
          </Row>
        )}
        <Divider style={{ margin: '8px 0', borderColor: COLORS.lightBorder }} />
        <Row justify="space-between">
          <Col><Text strong style={{ fontSize: 16, color: COLORS.ink }}>合计</Text></Col>
          <Col>
            <Text strong style={{ fontSize: 18, color: COLORS.cinnabar }}>
              {formatPrice(total)}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* 按钮组 */}
      <Space>
        <Button
          onClick={() => setCurrentStep(0)}
          style={{ borderRadius: 8, borderColor: COLORS.lightBorder, color: COLORS.lightInk }}
        >
          上一步
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={() => setCurrentStep(2)}
          style={{
            borderRadius: 8,
            background: COLORS.teaBrown,
            borderColor: COLORS.teaBrown,
          }}
        >
          下一步：选择支付
        </Button>
      </Space>
    </div>
  );

  /** 步骤 3 内容：支付方式 */
  const renderStep3 = () => (
    <div>
      {/* 价格汇总摘要 */}
      <Card
        size="small"
        style={{
          marginBottom: 24,
          borderRadius: 10,
          borderColor: COLORS.lightBorder,
        }}
      >
        <Row justify="space-between">
          <Col><Text strong style={{ color: COLORS.ink }}>订单总额：</Text></Col>
          <Col>
            <Text strong style={{ fontSize: 20, color: COLORS.cinnabar }}>
              {formatPrice(total)}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* 支付方式选择 */}
      <Title level={5} style={{ marginBottom: 16, color: COLORS.ink }}>
        <WalletOutlined style={{ marginRight: 8, color: COLORS.teaBrown }} />
        选择支付方式
      </Title>

      <Radio.Group
        value={payVendor}
        onChange={(e) => setPayVendor(e.target.value)}
        style={{ marginBottom: 32 }}
      >
        <Space direction="vertical" size={12}>
          {/* <Radio value="alipay">
            <Space>
              <img
                src="https://img.alicdn.com/imgextra/i4/O1CN01FEHJAn1PvNNbNJRaN_!!6000000001903-55-tps-200-200.svg"
                alt="支付宝"
                style={{ height: 20, verticalAlign: 'middle' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              支付宝
            </Space>
          </Radio>
          <Radio value="wechat">
            <Space>微信支付</Space>
          </Radio> */}
          <Radio value="unionpay">
            <Space>银联支付</Space>
          </Radio>
        </Space>
      </Radio.Group>

      {/* 按钮组 */}
      <Space>
        <Button
          onClick={() => setCurrentStep(1)}
          style={{ borderRadius: 8, borderColor: COLORS.lightBorder, color: COLORS.lightInk }}
        >
          上一步
        </Button>
        <Button
          type="primary"
          size="large"
          icon={<CheckCircleOutlined style={{ color: COLORS.teaBrown }} />}
          loading={submitting}
          onClick={handlePlaceOrder}
          style={{
            minWidth: 160,
            borderRadius: 8,
            background: COLORS.teaBrown,
            borderColor: COLORS.teaBrown,
          }}
        >
          确认下单并支付
        </Button>
      </Space>
    </div>
  );

  // 步骤配置
  const steps = [
    { title: '收货地址', content: renderStep1 },
    { title: '订单确认', content: renderStep2 },
    { title: '支付方式', content: renderStep3 },
  ];

  return (
    <StorefrontLayout>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px 64px' }}>
        <Title
          level={2}
          style={{
            marginBottom: 32,
            color: COLORS.ink,
            fontSize: 24,
            letterSpacing: '0.15em',
          }}
        >
          结算
        </Title>

        {/* 步骤条 */}
        <Steps
          current={currentStep}
          style={{ marginBottom: 40 }}
          items={steps.map((s) => ({ title: s.title }))}
          labelPlacement="vertical"
          size="small"
          progressDot={{
            dotStyle: { background: COLORS.teaBrown, borderColor: COLORS.teaBrown },
          }}
        />

        {/* 当前步骤内容 */}
        <Card
          style={{
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          {steps[currentStep]?.content()}
        </Card>
      </div>
    </StorefrontLayout>
  );
}
